import { useEffect, useState, useSyncExternalStore } from "react";
import { BASE_URL } from "../Constants";
import api from "../components/login/interceptors";

// Plans and billing API (Sift Hire billing guide). Nothing about plans, prices, limits, features
// or add-ons is defined here: names, numbers and feature lists all come from the responses.
// Money is integer cents and dates are ISO-8601 UTC. `null` in a limit means unlimited.

const PLANS_URL = `${BASE_URL}/api/plans`;
const PLAN_URL = `${BASE_URL}/api/organisation/plan`;
const ADMIN_URL = `${BASE_URL}/api/admin`;

const DAY = 86400000;

// Days before a trial ends at which the banner turns into a warning. The API has no setting for
// this yet, so it lives here in one place.
export const TRIAL_WARNING_DAYS = [7, 3, 1];

// Show a usage meter in the warning colour from this share of the limit
export const NEAR_LIMIT_RATIO = 0.8;

// Metric keys are the API's. Labels are display copy; a metric the API adds later still renders
// through `metricLabel`'s fallback.
export const METRICS = [
  { key: "ai_credits", label: "AI interview credits", addonType: "ai_credit" },
  { key: "resume_parses", label: "Resume parsing", addonType: "resume_pack" },
  { key: "job_slots", label: "Active jobs", addonType: null },
  { key: "recruiter_seats", label: "Recruiter seats", addonType: "recruiter_seat" },
  { key: "panelist_seats", label: "Panelist seats", addonType: "panelist_seat" },
];

const titleCase = (key) => String(key || "").replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());

export const metricLabel = (key) => METRICS.find((metric) => metric.key === key)?.label || titleCase(key);
// Short noun for a limit in a plan card ("3 active jobs"); the number and period come from the plan
const NOUNS = {
  job_slots: "active jobs",
  recruiter_seats: "recruiter seats",
  panelist_seats: "panelist seats",
  ai_credits: "AI credits",
  resume_parses: "resume parses",
};
export const metricNoun = (key) => NOUNS[key] || titleCase(key).toLowerCase();

// What an add-on gives, in one short phrase: "+1 AI credit", "+2 recruiter seats", "+500 resume parses".
// `units` is the total it adds (quantity times pack size), as the API reports it.
export const addonGain = (units, key) => `+${Number(units || 0).toLocaleString("en-US")} ${Number(units) === 1 ? metricNoun(key).replace(/s$/, "") : metricNoun(key)}`;

// "20 AI credits / month", "1 active job in total", "Unlimited active jobs"
export const limitPhrase = (plan, key) => {
  const value = plan?.limits?.[key];
  const period = plan?.limit_periods?.[key];
  const amount = value === null || value === undefined ? "Unlimited" : value.toLocaleString("en-US");
  const noun = value === 1 ? metricNoun(key).replace(/s$/, "") : metricNoun(key);
  return `${amount} ${noun}${period === "period" ? " / month" : period === "total" ? " in total" : ""}`;
};

export const metricForAddon = (addonType) => METRICS.find((metric) => metric.addonType === addonType)?.key || null;

// Labels for feature flags the API has defined so far. Unknown flags fall back to their key.
const FEATURE_LABELS = {};
export const featureLabel = (key) => FEATURE_LABELS[key] || titleCase(key);

// Feature flags the API still sends that are not shown in the plan UI.
const HIDDEN_FEATURES = ["marketplace"];
export const isShownFeature = (key) => !HIDDEN_FEATURES.includes(key);

export const PLAN_STATE = {
  LEGACY: "legacy",
  TRIAL: "trial",
  ACTIVE: "active",
  PAST_DUE: "past_due",
  ENDING: "ending",
  NONE: "none",
};

// One reading of the `plan` object, used by every screen so they never disagree
export const planState = (plan) => {
  if (plan === null || plan === undefined) return PLAN_STATE.LEGACY;
  if (plan.active === false) return PLAN_STATE.NONE;
  if (plan.is_trial) return PLAN_STATE.TRIAL;
  if (plan.status === "past_due") return PLAN_STATE.PAST_DUE;
  if (plan.cancel_at_period_end) return PLAN_STATE.ENDING;
  return PLAN_STATE.ACTIVE;
};

// Has the plan run out? The server says so with `active: false`, but it can lag behind a plan's end date (it ends
// plans in the background), so a plan whose own end date has passed counts as ended here too. A renewing paid plan
// has no end date, so it is never caught by this.
export const planEnded = (plan, now = Date.now()) => {
  if (!plan) return false;
  if (plan.active === false) return true;
  const end = plan.end_at ? new Date(plan.end_at).getTime() : NaN;
  return Number.isFinite(end) && end <= now;
};

// The plan row (active, or the last one when there is none)
export const planOf = (plan) => (plan?.active === false ? plan.last_plan || null : plan || null);

export const planDisplayName = (plan) => planOf(plan)?.name || "—";

// Keys StatusBadge understands
export const planStatusKey = (plan) => {
  switch (planState(plan)) {
    case PLAN_STATE.TRIAL:
      return "trial_active";
    case PLAN_STATE.ACTIVE:
      return "active";
    case PLAN_STATE.PAST_DUE:
      return "past_due";
    case PLAN_STATE.ENDING:
      return "cancel_scheduled";
    case PLAN_STATE.NONE:
      // Say why it ended. Only a plan that was really cancelled is "Cancelled"; anything unknown is just "Ended".
      return (
        {
          trial_expired: "trial_expired",
          canceled: "cancelled",
          payment_failed: "payment_failed",
          expired: "expired",
        }[plan.last_plan?.ended_reason] || "ended"
      );
    default:
      return "no_plan";
  }
};

// The dates line for a plan that is still running: "Started Oct 29, 2026 · Ends Nov 29, 2026".
// A trial or a plan that is cancelling has an end date; a paid plan ends at the close of its current period.
export const runningPlanSpan = (plan) => {
  const parts = [`Started ${formatDate(plan.start_at)}`];
  if (plan.is_trial || plan.cancel_at_period_end) {
    if (plan.end_at) parts.push(`Ends ${formatDate(plan.end_at)}`);
  } else if (plan.current_period_end) {
    parts.push(`Ends ${formatDate(plan.current_period_end)}`);
  } else if (plan.end_at) {
    parts.push(`Ends ${formatDate(plan.end_at)}`);
  }
  return parts.join(" · ");
};

// The API flags an organisation as custom the first time its limits are edited and keeps the flag even if the
// numbers are put back. So "custom" means the flag is set AND at least one limit differs from the plan's own.
// Without the plan to compare against (it isn't in the list), the flag is all there is to go on.
export const hasCustomLimits = (plan, catalogPlan) => {
  if (!plan?.custom_config) return false;
  if (!catalogPlan?.limits || !plan.base_limits) return true;
  return METRICS.some(({ key }) => (plan.base_limits[key] ?? null) !== (catalogPlan.limits[key] ?? null));
};

// Page numbers to show in a pager: the first, the last and the current page with its neighbours, and "…"
// where pages are skipped. Few pages are all shown.
export const pageWindow = (page, pages) => {
  if (pages <= 5) return Array.from({ length: pages }, (_, index) => index + 1);
  const shown = [...new Set([1, pages, page - 1, page, page + 1])].filter((n) => n >= 1 && n <= pages).sort((a, b) => a - b);
  return shown.flatMap((n, index) => (index > 0 && n - shown[index - 1] > 1 ? ["…", n] : [n]));
};

// Whole days from the server's own count when it sends one, otherwise from the end date
export const trialDaysLeft = (plan, now = Date.now()) => {
  if (typeof plan?.trial_days_left === "number") return Math.max(0, plan.trial_days_left);
  if (!plan?.end_at) return null;
  return Math.max(0, Math.ceil((new Date(plan.end_at).getTime() - now) / DAY));
};

/* ---------- Formatting ---------- */

export const formatDate = (value) => {
  const date = new Date(value);
  if (!value || Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
};

export const formatCents = (cents, currency = "usd") => {
  if (typeof cents !== "number") return "—";
  const amount = cents / 100;
  return amount.toLocaleString("en-US", {
    style: "currency",
    currency: String(currency || "usd").toUpperCase(),
    minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
  });
};

export const formatNumber = (value) => (typeof value === "number" ? value.toLocaleString("en-US") : value);

export const formatLimit = (value) => (value === null || value === undefined ? "Unlimited" : formatNumber(value));

// yyyy-mm-dd in local time, what <input type="date"> reads and writes
export const toDateInput = (value) => {
  const date = new Date(value);
  if (!value || Number.isNaN(date.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

// A trial runs to the end of its last day
export const endOfDay = (dateInput) => new Date(`${dateInput}T23:59:59`).toISOString();

/* ---------- Public plan helpers ---------- */

export const monthlyPrice = (plan) => plan?.prices?.monthly || null;
export const monthlyCents = (plan) => monthlyPrice(plan)?.amount_cents ?? null;

export const sortPlans = (plans) =>
  [...(plans || [])].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));

// "$49 / month", "Contact us" for custom-priced plans
export const priceLabel = (plan) => {
  const price = monthlyPrice(plan);
  if (plan?.contact_sales || !price) return "Contact us";
  return `${formatCents(price.amount_cents, price.currency)} / month`;
};

// What buying one unit of an add-on costs, in the plan's own wording
export const addonPriceLabel = (addon) => {
  if (!addon) return "—";
  const price = formatCents(addon.amount_cents);
  return addon.billing === "recurring" ? `${price} / month` : `${price} one time`;
};

// Higher price than the current plan; plans without a fixed price are never "an upgrade"
export const isUpgradeFrom = (currentPlan, target) => {
  const from = monthlyCents(currentPlan);
  const to = monthlyCents(target);
  if (to === null || target.contact_sales) return false;
  return from === null ? true : to > from;
};

export const limitChanges = (fromLimits, toLimits) =>
  METRICS.map(({ key, label }) => ({
    key,
    label,
    from: fromLimits ? fromLimits[key] : undefined,
    to: toLimits ? toLimits[key] : undefined,
  })).filter((row) => row.from !== row.to);

/* ---------- Entitlements ---------- */

// Is this action allowed for the organisation right now? `metric` null checks only that a plan is active.
// While nothing has loaded the backend decides, so a slow request never locks the app.
export const evaluateGate = (data, metric = null, count = 1) => {
  if (!data) return { allowed: true, reason: null };
  const plan = data.plan;
  // No plan at all (never had one) is blocked the same as an ended plan: there is nothing to use yet
  if (!plan || plan.active === false) return { allowed: false, reason: "no_plan" };
  if (!metric) return { allowed: true, reason: null };
  const usage = data.usage?.[metric];
  if (!usage) return { allowed: true, reason: null };
  // Same rule as the usage cards: a missing `remaining` is worked out from limit and used; no limit means unlimited
  const capped = usage.limit !== null && usage.limit !== undefined;
  const remaining = usage.remaining ?? (capped ? Math.max(0, usage.limit - (usage.used || 0)) : null);
  if (remaining === null || remaining === undefined) return { allowed: true, reason: null };
  if (remaining >= count) return { allowed: true, reason: null };
  return { allowed: false, reason: "limit", metric };
};

export const nearLimit = (usage) =>
  Boolean(usage) && usage.limit !== null && usage.limit > 0 && usage.used / usage.limit >= NEAR_LIMIT_RATIO;

// What to tell someone whose action was refused. Only billing managers are pointed at plans or prices.
export const blockedMessage = ({ reason, metric, canBill }) => {
  if (reason === "no_plan") {
    return canBill
      ? "Your plan has ended. Choose a plan to continue."
      : "Your organisation's subscription has expired. Contact your Organisation Admin to restore access.";
  }
  const what = metric ? metricLabel(metric).toLowerCase() : "plan";
  return canBill
    ? `You've reached your plan's ${what} limit. Upgrade or add more to continue.`
    : `Your organisation has reached its ${what} limit. Contact your Organisation Admin.`;
};

/* ---------- Plan store ---------- */

// The signed-in organisation's plan, usage and add-ons. One copy shared by the banner, the gated
// buttons and the billing page; the server stays the source of truth and it is re-read on focus.
let state = { data: null, status: "idle", error: null, loadedAt: 0 };
const listeners = new Set();
let inflight = null;

const setState = (patch) => {
  state = { ...state, ...patch };
  listeners.forEach((listener) => listener());
};
const subscribe = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const usePlan = () => useSyncExternalStore(subscribe, () => state);

export const refreshPlan = () => {
  if (inflight) return inflight;
  if (!state.data) setState({ status: "loading" });
  inflight = api
    .get(PLAN_URL)
    .then((response) => {
      setState({ data: response.data, status: "ready", error: null, loadedAt: Date.now() });
      return response.data;
    })
    .catch((error) => {
      // A failed check is not proof the plan ended: keep what was last known and flag the error
      setState({ status: state.data ? "ready" : "error", error });
      throw error;
    })
    .finally(() => {
      inflight = null;
    });
  return inflight;
};

// Add-on and plan responses carry fresh plan, usage and addons
export const applyPlanResponse = (body = {}) => {
  if (!body.usage) return;
  setState({
    data: { plan: body.plan ?? state.data?.plan ?? null, read_only: Boolean(body.read_only), usage: body.usage, addons: body.addons || [] },
    status: "ready",
    error: null,
    loadedAt: Date.now(),
  });
};

export const resetPlanStore = () => {
  inflight = null;
  state = { data: null, status: "idle", error: null, loadedAt: 0 };
  listeners.forEach((listener) => listener());
};

/* ---------- Organisation API ---------- */

export const apiErrorCode = (error) => error?.response?.data?.code || "";
export const apiErrorMessage = (error, fallback) => error?.response?.data?.error || fallback;

export const fetchPublicPlans = async () => sortPlans((await api.get(PLANS_URL)).data?.plans);

export const fetchPlanHistory = async () => (await api.get(`${PLAN_URL}/history`)).data?.plans || [];

export const fetchPayments = async ({ page = 1, limit = 50 } = {}) =>
  (await api.get(`${PLAN_URL}/payments`, { params: { page, limit } })).data || { payments: [], total: 0 };

// Stripe Checkout for a new plan or a change; the plan only switches once the webhook lands
export const startCheckout = async (planId) =>
  (await api.post(`${PLAN_URL}/checkout`, { plan_id: planId, billing_cycle: "monthly" })).data;

// Seats are charged to the saved card at once and answer with fresh usage; credits and
// resume packs answer with a checkout_url instead
export const buyAddon = async (addonType, quantity) => {
  const body = (await api.post(`${PLAN_URL}/addons`, { addon_type: addonType, quantity })).data || {};
  if (!body.checkout_url) applyPlanResponse(body);
  return body;
};

// What a seat would cost right now: the prorated amount due now and when the period ends. Charges nothing.
export const previewAddon = async (addonType, quantity) =>
  (await api.post(`${PLAN_URL}/addons/preview`, { addon_type: addonType, quantity })).data || {};

export const removeAddon = async (addonId) => {
  const body = (await api.delete(`${PLAN_URL}/addons/${addonId}`)).data || {};
  applyPlanResponse(body);
  return body;
};

export const openBillingPortal = async () => (await api.post(`${PLAN_URL}/portal`)).data?.portal_url || "";

// Poll after returning from Stripe until `isDone` sees the webhook's result, up to 30 s.
// Resolves true when it landed, false when it is still pending.
export const waitForPlanChange = async (isDone, { intervalMs = 2000, timeoutMs = 30000 } = {}) => {
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    try {
      const data = await refreshPlan();
      if (isDone(data)) return true;
    } catch {
      // Keep polling; one failed read isn't a failed payment
    }
    await new Promise((resolve) => setTimeout(resolve, intervalMs));
  }
  return false;
};

// Stripe sends people back with a full page load, so the plan as it was before checkout is kept
// for the return trip. Without it "has the webhook landed yet?" has nothing to compare against.
const BASELINE_KEY = "sifthire:checkout-baseline";

export const rememberCheckoutBaseline = () => {
  try {
    sessionStorage.setItem(BASELINE_KEY, JSON.stringify({ plan: state.data?.plan ?? null, addons: state.data?.addons || [] }));
  } catch {
    // Without storage the return page just shows the current plan once
  }
};

export const takeCheckoutBaseline = () => {
  try {
    const raw = sessionStorage.getItem(BASELINE_KEY);
    sessionStorage.removeItem(BASELINE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

// True once the plan differs from `before` or an add-on appears that wasn't there
export const planChanged = (before, now) => {
  if (!now) return false;
  if (planOf(before?.plan)?.plan_id !== planOf(now.plan)?.plan_id || planState(now.plan) !== planState(before?.plan)) return true;
  const known = new Set((before?.addons || []).map((addon) => addon.id));
  return (now.addons || []).some((addon) => !known.has(addon.id));
};

/* ---------- Super Admin API ---------- */

const orgPlanUrl = (orgId) => `${ADMIN_URL}/organisations/${orgId}/plan`;

export const fetchAdminPlans = async () => (await api.get(`${ADMIN_URL}/plans`)).data?.plans || [];

export const updateAdminPlan = async (planId, body) => (await api.patch(`${ADMIN_URL}/plans/${planId}`, body)).data;

export const fetchBillingSettings = async () => (await api.get(`${ADMIN_URL}/billing-settings`)).data?.settings || {};

export const saveBillingSettings = async (body) => (await api.patch(`${ADMIN_URL}/billing-settings`, body)).data?.settings || {};

export const patchOrgPlan = async (orgId, body) => {
  const data = (await api.patch(orgPlanUrl(orgId), body)).data;
  invalidateAdminOrgPlan(orgId);
  return data;
};

export const assignOrgPlan = async (orgId, body) => {
  const data = (await api.post(orgPlanUrl(orgId), body)).data;
  invalidateAdminOrgPlan(orgId);
  return data;
};

export const fetchAdminOrgPayments = async (orgId, { page = 1, limit = 20 } = {}) =>
  (await api.get(`${orgPlanUrl(orgId)}/payments`, { params: { page, limit } })).data || { payments: [], total: 0 };

// One request per organisation however many components ask, so the table can show a plan per row
const adminPlanCache = new Map();
const adminPlanListeners = new Set();

export const fetchAdminOrgPlan = (orgId) => {
  if (!adminPlanCache.has(orgId)) {
    const request = api.get(orgPlanUrl(orgId)).then((response) => response.data);
    request.catch(() => adminPlanCache.delete(orgId));
    adminPlanCache.set(orgId, request);
  }
  return adminPlanCache.get(orgId);
};

export const invalidateAdminOrgPlan = (orgId) => {
  adminPlanCache.delete(orgId);
  adminPlanListeners.forEach((listener) => listener(orgId));
};

// { data, loading, error } for one organisation, refreshed when an admin edit invalidates it
export const useAdminOrgPlan = (orgId) => {
  const [result, setResult] = useState({ data: null, loading: Boolean(orgId), error: false });
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const listener = (changedId) => changedId === orgId && setTick((value) => value + 1);
    adminPlanListeners.add(listener);
    return () => adminPlanListeners.delete(listener);
  }, [orgId]);

  useEffect(() => {
    if (!orgId) return undefined;
    let cancelled = false;
    setResult((previous) => ({ ...previous, loading: true, error: false }));
    fetchAdminOrgPlan(orgId)
      .then((data) => !cancelled && setResult({ data, loading: false, error: false }))
      .catch(() => !cancelled && setResult({ data: null, loading: false, error: true }));
    return () => {
      cancelled = true;
    };
  }, [orgId, tick]);

  return result;
};

/* ---------- Form validation ---------- */

// Blank means unlimited. Returns null when fine, otherwise the message to show.
export const limitError = (value, used = 0) => {
  const text = String(value ?? "").trim();
  if (!text) return null;
  if (!/^\d+$/.test(text)) return "Use a whole number, or leave blank for unlimited.";
  if (Number(text) > 10000000) return "That is more than the system allows.";
  if (Number(text) < used) return `Can't be lower than the ${formatNumber(used)} already used.`;
  return null;
};

export const toLimit = (value) => {
  const text = String(value ?? "").trim();
  return text === "" ? null : Number(text);
};

export const trialDaysError = (value) => {
  const text = String(value ?? "").trim();
  if (!/^\d+$/.test(text)) return "Enter a whole number of days.";
  if (Number(text) < 1 || Number(text) > 365) return "Trials run between 1 and 365 days.";
  return null;
};

/* ---------- Status banner ---------- */

export const PLAN_PATH = "/settings?tab=plan";
export const UPGRADE_PATH = `${PLAN_PATH}&upgrade=1`;

const ENDED_TITLES = {
  trial_expired: "Your free trial has ended",
  payment_failed: "Your plan ended because the payment didn't go through",
  canceled: "Your plan was cancelled",
  expired: "Your plan has expired",
};

// What to show for the current plan state, or null when there is nothing to say.
// Only people who manage billing get pricing and purchase actions.
export const bannerFor = (plan, canBill, now = Date.now()) => {
  switch (planState(plan)) {
    case PLAN_STATE.TRIAL: {
      const days = trialDaysLeft(plan, now);
      if (days === null) return null;
      // First threshold turns the banner into a warning, the second makes it stronger
      const [warnAt, urgentAt] = TRIAL_WARNING_DAYS;
      const soon = days <= warnAt;
      const urgent = days <= urgentAt;
      const unit = days === 1 ? "day" : "days";
      return {
        tone: urgent ? "danger" : soon ? "warn" : "trial",
        title: !soon
          ? `Your free trial is active — ${days} ${unit} remaining`
          : days === 0
            ? "Your trial ends today"
            : `Your trial ends in ${days} ${unit}`,
        body: canBill
          ? soon
            ? "Upgrade your organisation's plan to continue creating jobs, scheduling interviews, and using premium features."
            : "You're currently exploring the platform with your free trial. Upgrade anytime to continue uninterrupted."
          : "Your organisation is on a free trial. Ask your Organisation Admin to choose a plan before it ends.",
        action: canBill ? { label: soon ? "Upgrade Plan" : "Explore Plans", to: soon ? UPGRADE_PATH : PLAN_PATH } : null,
      };
    }
    case PLAN_STATE.PAST_DUE:
      return {
        tone: "warn",
        title: canBill ? "We couldn't charge your card for the renewal" : "Your organisation's last payment didn't go through",
        body: canBill
          ? "Everything keeps working while Stripe retries. Update your payment method to avoid an interruption."
          : "Ask your Organisation Admin to update the payment method.",
        action: canBill ? { label: "Update card", portal: true } : null,
      };
    case PLAN_STATE.ENDING:
      if (!canBill) return null;
      return {
        tone: "warn",
        title: `Your ${planDisplayName(plan)} plan ends on ${formatDate(plan.end_at)}`,
        body: "You keep full access until then. Choose a plan to carry on afterwards.",
        action: { label: "Choose a plan", to: UPGRADE_PATH },
      };
    case PLAN_STATE.NONE:
      return canBill
        ? {
            tone: "danger",
            title: ENDED_TITLES[plan.last_plan?.ended_reason] || "Your organisation doesn't have an active plan",
            body: "",
            action: { label: "Buy Plan", to: UPGRADE_PATH },
          }
        : {
            tone: "danger",
            title: "Your organisation's subscription has expired",
            body: "You can still view your existing information, but new activities are temporarily unavailable. Please contact your Organisation Admin to reactivate access.",
            action: null,
          };
    case PLAN_STATE.LEGACY:
      // An account that never had a plan. Only people who can buy are asked to; everyone else has nothing to do.
      return canBill
        ? {
            tone: "info",
            title: "You don't have a plan yet",
            body: "Buy a plan to start creating jobs, scheduling interviews and using the platform.",
            action: { label: "Buy Plan", to: UPGRADE_PATH },
          }
        : null;
    default:
      return null;
  }
};

/* ---------- AI credit cost of scheduling ---------- */

// An AI interview costs 1 credit per 30 minutes (so 1 hr = 2, 1.5 hr = 3, 2 hr = 4), rounded up, and is
// charged once, when AI is switched on for it. Switching between AI modes uses no more and switching AI
// off gives the credits back. The one place to change if the price changes.
export const MINUTES_PER_CREDIT = 30;
export const creditsForInterview = (minutes) => Math.max(1, Math.ceil((Number(minutes) || 0) / MINUTES_PER_CREDIT));

// What scheduling `count` AI interviews of `minutes` each does to the balance in `usage` (usage.ai_credits).
// `known` is false until the plan has loaded, so the form never blocks on a slow request.
export const creditSummary = ({ usage, count = 0, minutes = 0, planActive = true }) => {
  const perInterview = creditsForInterview(minutes);
  const cost = Math.max(0, count) * perInterview;
  if (!planActive) return { cost, perInterview, known: true, unlimited: false, remaining: 0, after: 0, covered: 0, short: cost > 0, none: cost > 0, inactive: true };
  if (!usage) return { cost, perInterview, known: false };
  const unlimited = usage.limit === null || usage.limit === undefined;
  const remaining = unlimited ? null : usage.remaining ?? Math.max(0, usage.limit - usage.used);
  const covered = unlimited ? count : Math.min(count, Math.floor(remaining / perInterview));
  return {
    cost,
    perInterview,
    known: true,
    limit: usage.limit,
    used: usage.used,
    unlimited,
    remaining,
    after: unlimited ? null : Math.max(0, remaining - cost),
    covered,
    short: cost > 0 && !unlimited && remaining < cost,
    none: cost > 0 && !unlimited && remaining < perInterview,
    inactive: false,
  };
};
